/* eslint-disable no-unused-vars */
/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, message, Spin } from "antd";
import { DropDownComponent, CalendarComponent } from "../Master/CommonComponents";
import moment from "moment";
import { getHubList } from "../../services/api/hub";
import { HubCPOverviewService } from "../../services/api/hubCPOverviewService";
import TopCard from "./TopCard";
import StatusDistribution from "./StatusDistribution";
import AgeingBuckets from "./AgeingBuckets";
import Exceptions from "./Exceptions";
import ShipmentDetailsModal from "./ShipmentDetailsModal";
import { TOP_CARD_NAMES } from "../../constants/hubManagerOverviewConstants";


const HubCPOverview = () => {
    const [hubList, setHubList] = useState([]);
    const navigate = useNavigate();
    const [selectedHubs, setSelectedHubs] = useState(null);
    const [hubUser] = useState(() => {
        try {
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            return user?.roles?.[0];
        } catch (e) {
            return null;
        }
    });
    const [hubLoading, setHubLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [dateRange, setDateRange] = useState(null);
    const ALL_HUBS_VALUE = "ALL_HUBS";



    // Transform dashboard data to card format
    const cards = useMemo(() => {
        if (!dashboardData || !Array.isArray(dashboardData)) {
            return [];
        }

        return dashboardData.map(item => ({
            id: item.title,
            name: item.title ? item.title.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) : "",
            value: item.totalCount || 0,
            visible: true,
            statuses: item.statuses || []
        }));
    }, [dashboardData]);

    // Fetch hub list on component mount
    useEffect(() => {
        const fetchHubList = async () => {
            try {
                setHubLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                message.error("Failed to fetch hub list");
                console.error("Error fetching hub list:", error);
            } finally {
                setHubLoading(false);
            }
        };

        fetchHubList();
    }, []);

    // Set user role and default hub for HUBADMIN/HUBOPERATOR users
    useEffect(() => {
        if (hubList.length === 0) return;

        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userId = JSON.parse(localStorage.getItem("userId") || "{}");
        const userRole = user?.roles?.[0];

        if (selectedHubs !== null) return;

        if (userRole === "ROLE_HUBADMIN" || userRole === "ROLE_HUBOPERATOR") {
            const defaultHub = hubList.find(
                (hub) => String(hub.value) === String(userId?.userInfo?.premiseId)
            );
            if (defaultHub) {
                setSelectedHubs(defaultHub.value);
            }
        } else {
            setSelectedHubs(null);
        }
    }, [hubList]);

    // Prepare options with "All Hubs" option
    const hubOptions = React.useMemo(() => {
        if (hubList.length === 0) return [];

        const allHubsOption = {
            label: "All Hubs",
            value: ALL_HUBS_VALUE,
        };

        return [allHubsOption, ...hubList];
    }, [hubList]);

    const areAllHubsSelected = React.useMemo(() => {
        return selectedHubs === null || selectedHubs === ALL_HUBS_VALUE;
    }, [selectedHubs]);

    const hubParamForAPI = React.useMemo(() => {
        return areAllHubsSelected ? null : (selectedHubs ? [selectedHubs] : null);
    }, [selectedHubs, areAllHubsSelected]);

    const displayValue = React.useMemo(() => {
        if (areAllHubsSelected) {
            return ALL_HUBS_VALUE;
        }
        return selectedHubs;
    }, [selectedHubs, areAllHubsSelected]);

    const handleHubChange = (value) => {
        if (!value || value === ALL_HUBS_VALUE) {
            setSelectedHubs(null);
            return;
        }
        setSelectedHubs(value);
    };

    const abortControllerRef = React.useRef(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            const { signal } = abortControllerRef.current;

            try {
                setDashboardLoading(true);

                if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs === null) {
                    setDashboardLoading(false);
                    return;
                }

                const hubParam = areAllHubsSelected ? null : (selectedHubs ? selectedHubs : null);
                const fromDate = dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null;
                const toDate = dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null;

                const [dashboardResponse, pieChartResponse] = await Promise.all([
                    HubCPOverviewService.getHubOverviewDashboard(hubParam, { signal }),
                    HubCPOverviewService.getPieChartHubManagerOverview(hubParam, fromDate, toDate, { signal })
                ]);

                if (dashboardResponse?.data) {
                    setDashboardData(dashboardResponse.data);
                } else {
                    setDashboardData([]);
                }

                if (pieChartResponse) {
                    setPieChartData(pieChartResponse);
                } else {
                    setPieChartData([]);
                }
            } catch (error) {
                if (error.name === 'CanceledError' || error.code === "ERR_CANCELED") {
                    console.log("Request canceled");
                    return;
                }
                message.error("Failed to fetch dashboard data");
                console.error("Error fetching dashboard data:", error);
                setDashboardData([]);
            } finally {
                if (!signal.aborted) {
                    setDashboardLoading(false);
                }
            }
        };

        fetchDashboardData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };

    }, [selectedHubs, areAllHubsSelected, dateRange]);

    const handleCardClick = async (card) => {
        const cardName = card.name.trim();
        const hubIdToUse = areAllHubsSelected ? null : selectedHubs;

        if (cardName.includes(TOP_CARD_NAMES.TOTAL_AVAILABLE_SHIPMENTS)) {
            navigate("/total-available-shipments-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.INBOUND_PENDENCY)) {
            navigate("/inbound-pendency-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_HUB)) {
            navigate("/outscan-pending-inscan-to-hub-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_CP)) {
            navigate("/outscan-pending-inscan-to-cp-clickable-report", { state: { hubId: hubIdToUse } });
            return;
        }
    };


    return (
        <div className="hub-cp-overview-container">
            {/* Unified Page Header + Filters */}
            <div className="page-topbar">
                <span className="title-main">Shipment Pendency</span>
                <div className="topbar-filters">
                    <DropDownComponent
                        style={{ width: 180 }}
                        placeholder="All Hubs"
                        options={hubOptions}
                        onChange={handleHubChange}
                        value={displayValue}
                        allowClear
                        mode="default"
                        disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                        maxTagCount="responsive"
                        showSearch={true}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label || "").toLowerCase().includes(input.toLowerCase())
                        }
                        loading={hubLoading}
                    />
                    <CalendarComponent
                        type="range"
                        value={dateRange}
                        onChange={(value) => setDateRange(value)}
                        format="YYYY-MM-DD"
                        allowClear={true}
                        size="small"
                        placeholder={["Start Date", "End Date"]}
                        className="topbar-date-picker"
                    />
                </div>
            </div>

            <Spin spinning={dashboardLoading}>
                <TopCard cards={cards} onCardClick={handleCardClick} loading={false} />

                <Row gutter={[16, 16]} className="dashboard-content-row">
                    <Col xs={24}>
                        <StatusDistribution
                            data={pieChartData}
                            selectedHubs={selectedHubs}
                            areAllHubsSelected={areAllHubsSelected}
                            dateRange={dateRange}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <AgeingBuckets />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Exceptions />
                    </Col>
                </Row>
            </Spin>
        </div>
    );
};

export default HubCPOverview;
